/**
 * Sentry facade — lazy-loads the real SDK (sentry-impl.ts) after first paint.
 *
 * @sentry/react with tracing + replay is ~90KB gz; importing it statically
 * put it in the entry bundle and pushed mobile LCP past the Lighthouse gate.
 * Calls made before the SDK lands are queued and flushed on load.
 */

type SentryModule = typeof import("./sentry-impl");

let mod: SentryModule | null = null;
let loading: Promise<SentryModule> | null = null;
const queue: Array<(m: SentryModule) => void> = [];

function load(): Promise<SentryModule> {
  if (!loading) {
    loading = import("./sentry-impl").then((m) => {
      mod = m;
      for (const fn of queue.splice(0)) fn(m);
      return m;
    });
  }
  return loading;
}

function withSentry(fn: (m: SentryModule) => void): void {
  if (mod) {
    fn(mod);
    return;
  }
  queue.push(fn);
  void load();
}

/** Defer SDK init until the browser is idle after load — never block paint. */
export function initSentry(): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const start = () => {
    void load().then((m) => m.initSentry());
  };
  if (document.readyState === "complete") {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(start, { timeout: 4000 });
    } else {
      setTimeout(start, 2000);
    }
  } else {
    window.addEventListener("load", () => setTimeout(start, 1500), { once: true });
  }
}

export function setSentryUser(user: { id: string; email?: string } | null): void {
  withSentry((m) => m.setSentryUser(user));
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  withSentry((m) => m.captureException(error, context));
}

export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>): void {
  withSentry((m) => m.addBreadcrumb(message, category, data));
}
